import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Box } from '@mui/material'
import { useSelector } from 'src/utils/store'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { actions } from '../../TransfersState'
import * as S from './CagesManagementFilters.styles'
import { CageManagementStatusFilters } from '../../TransfersTypes'
import { defaultFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { getEnumKeyByValue } from 'src/utils/enum'
import { selectFulfillmentCenters } from 'src/features/selector'
import { actions as appActions } from 'src/features/state'

interface CagesManagementFiltersFormData {
  idStatus: number
  initialDate: string
  finalDate: string
  idSourceFc?: number
  idDestinationFc?: number
  cageAddress?: string
  invoiceAccessKey?: string
}

const CagesManagementFilters = () => {
  const { filtersSearchLists } = useSelector((state) => state.transfers)
  const fulfillmentCenters = useSelector(selectFulfillmentCenters)

  const dispatch = useDispatch()

  const [preventDoubleClick, setPreventDoubleClick] = useState(false)

  useEffect(() => {
    dispatch(appActions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
  }, [dispatch])

  useEffect(() => {
    dispatch(actions.getSearchFilters())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { fcs } = filtersSearchLists

  const {
    register, handleSubmit, formState: { errors }, watch, trigger, clearErrors,
  } = useForm<CagesManagementFiltersFormData>()

  const initialDate = watch('initialDate')
  const finalDate = watch('finalDate')

  const statusOptions = useMemo(() => Object.values(CageManagementStatusFilters).map((status, index) => ({
    id: index + 1,
    description: status,
  })), [])

  const sourceFcs = useMemo(() => fcs.map((fc) => ({
    id: fc.id,
    description: fc.name,
  })), [fcs])

  const destinationFcs = useMemo(() => fcs.map((fc) => ({
    id: fc.id,
    description: fc.name,
  })), [fcs])

  const validateFcAccess = (sourceFcId: number | undefined, destinationFcId: number | undefined) => {
    const userFcIds = fulfillmentCenters.map((fc) => fc.id)
    const isSourceValid = sourceFcId && userFcIds.includes(sourceFcId)
    const isDestinationValid = destinationFcId && userFcIds.includes(destinationFcId)

    return isSourceValid || isDestinationValid
  }

  const onSubmit = (formData: CagesManagementFiltersFormData) => {
    if (preventDoubleClick) {
      return
    }

    clearErrors(['idSourceFc', 'idDestinationFc'])

    const {
      idStatus, idSourceFc, idDestinationFc, cageAddress, invoiceAccessKey,
    } = formData

    if (idSourceFc && idDestinationFc && !validateFcAccess(idSourceFc, idDestinationFc)) {
      toast.error('Você precisa ter acesso a pelo menos um FC (origem ou destino) selecionado')
      return
    }

    setPreventDoubleClick(true)

    const statusDescription = statusOptions.find((option) => option.id === idStatus)?.description
    const status = Object.entries(CageManagementStatusFilters).find(([, value]) => value === statusDescription)?.[0]

    const payload = {
      status: status || getEnumKeyByValue(CageManagementStatusFilters, CageManagementStatusFilters.ALL_STATUS),
      initialDate,
      finalDate,
      idSourceFc: idSourceFc || undefined,
      idDestinationFc: idDestinationFc || undefined,
      cageAddress: cageAddress || undefined,
      invoiceAccessKey: invoiceAccessKey || undefined,
      onFinally: () => setPreventDoubleClick(false),
    }

    dispatch(actions.searchCagesByFilters(payload))
  }

  const defaultInitialDate = () => {
    const today = new Date()
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
    return sevenDaysAgo.toISOString().split('T')[0]
  }

  useEffect(() => {
    clearErrors('initialDate')
    clearErrors('finalDate')
    const validateDates = async () => {
      await trigger('initialDate')
      await trigger('finalDate')
    }
    validateDates()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate, finalDate])

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '1.2rem 1rem',
        width: '100%',
        margin: '1.4rem 0 1.3rem 0',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.4rem 1rem',
        }}
      >
        <S.CustomSelect
          name='idStatus'
          inputProps={{
            ...register('idStatus'),
          }}
          autoComplete='off'
          id='idStatus'
          label='Status'
          variant='outlined'
          options={statusOptions}
          error={!!errors.idStatus}
          helperText={errors.idStatus?.message}
          type='text'
          fullWidth={false}
          autoFocus
          optionNone={false}
          maxHeight='600px'
          dataTestId='status-test-id'
          defaultValue={statusOptions[0].id}
        />
        <S.CustomTextField
          name='initialDate'
          id='initialDate'
          label='Data inicial'
          type='date'
          InputLabelProps={{ shrink: true }}
          inputProps={{
            ...register(
              'initialDate', {
                required: 'Campo obrigatório',
                validate: (value) => {
                  if (finalDate && value) {
                    const initialDateAux = new Date(value)
                    const finalDateAux = new Date(finalDate)
                    if (initialDateAux > finalDateAux) {
                      return 'Data inicial posterior à final'
                    }
                    const diffInTime = finalDateAux.getTime() - initialDateAux.getTime()
                    const diffInDays = diffInTime / (1000 * 60 * 60 * 24)
                    if (diffInDays > 30) {
                      return 'Diferença maior que 30 dias'
                    }
                  }
                  return true
                },
              },
            ),
            'data-testid': 'initial-date-test-id',
          }}
          error={!!errors.initialDate}
          helperText={errors.initialDate?.message}
          defaultValue={defaultInitialDate()}
        />
        <S.CustomTextField
          name='finalDate'
          id='finalDate'
          label='Data final'
          type='date'
          InputLabelProps={{ shrink: true }}
          inputProps={{
            ...register(
              'finalDate', {
                required: 'Campo obrigatório',
                validate: (value) => {
                  if (initialDate && value) {
                    const initialDateAux = new Date(initialDate)
                    const finalDateAux = new Date(value)
                    if (finalDateAux < initialDateAux) {
                      return 'Data final anterior à inicial'
                    }
                    const diffInTime = finalDateAux.getTime() - initialDateAux.getTime()
                    const diffInDays = diffInTime / (1000 * 60 * 60 * 24)
                    if (diffInDays > 30) {
                      return 'Diferença maior que 30 dias'
                    }
                  }
                  return true
                },
              },
            ),
            'data-testid': 'final-date-test-id',
          }}
          error={!!errors.finalDate}
          helperText={errors.finalDate?.message}
          defaultValue={new Date().toISOString().split('T')[0]}
        />
        <S.CustomSelect
          name='idSourceFc'
          inputProps={{
            ...register('idSourceFc'),
          }}
          autoComplete='off'
          id='idSourceFc'
          label='FC de Origem'
          variant='outlined'
          options={sourceFcs}
          error={!!errors.idSourceFc}
          helperText={errors.idSourceFc?.message}
          optionNone
          type='text'
          fullWidth={false}
          maxHeight='600px'
          dataTestId='source-fc-test-id'
        />
        <S.CustomSelect
          name='idDestinationFc'
          inputProps={{
            ...register('idDestinationFc'),
          }}
          autoComplete='off'
          id='idDestinationFc'
          label='FC de Destino'
          variant='outlined'
          options={destinationFcs}
          error={!!errors.idDestinationFc}
          helperText={errors.idDestinationFc?.message}
          optionNone
          type='text'
          fullWidth={false}
          maxHeight='600px'
          dataTestId='destination-fc-test-id'
        />
        <S.CustomTextField
          name='cageAddress'
          id='cageAddress'
          label='Endereço da Gaiola'
          type='text'
          inputProps={{
            ...register('cageAddress'),
            'data-testid': 'cage-address-test-id',
          }}
          error={!!errors.cageAddress}
          helperText={errors.cageAddress?.message}
        />
        <S.CustomTextField
          name='invoiceAccessKey'
          id='invoiceAccessKey'
          label='Chave de Acesso da Nota'
          type='text'
          inputProps={{
            ...register('invoiceAccessKey'),
            'data-testid': 'invoice-access-key-test-id',
          }}
          error={!!errors.invoiceAccessKey}
          helperText={errors.invoiceAccessKey?.message}
          width='31rem'
        />
      </Box>
      <S.CustomButton
        title='Buscar'
        type='submit'
        bgColor='blue'
        hoverColor='darkBlueHover'
        letterColor='white'
        data-testid='search-cages-button-test-id'
        disabled={preventDoubleClick}
      />
    </Box>
  )
}

export default CagesManagementFilters
